// ============================================================================
// Manual pending-task inbox — every manual provider op (Porter / intercity) awaiting
// the ops team's confirmation. This is the WHOLE signal to the team (no push
// notifications): book / cancel / modify a consignment on the partner's app, then
// confirm it here. Each confirm settles the op and updates the booking.
// ============================================================================
import { ReloadOutlined } from "@ant-design/icons";
import { Button, Space, Switch, Table, Tag, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { fmtIST } from "../../format";
import relativeTime from "dayjs/plugin/relativeTime";
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PendingManualOp } from "../../types";
import ExpressTag from "../../components/express_tag";
import UnloadingTag from "../../components/unloading_tag";
import { listPendingTasks } from "../logistics.api";
import ConfirmOpModal from "./confirm_op_modal";

interface Props {
  onCountChange?: (count: number) => void; // keep the tab badge in sync
  onChanged?: () => void; // a confirm settled an op — parent reloads the bookings tab
}

const OP_TAG: Record<string, { color: string; label: string }> = {
  book: { color: "blue", label: "Book" },
  cancel: { color: "red", label: "Cancel" },
  update: { color: "gold", label: "Modify" },
};

dayjs.extend(relativeTime);

// Sum of every column width below — `scroll.x` must match it exactly or the right-pinned
// Action column overlaps its neighbour.
const TABLE_WIDTH = 100 + 320 + 130 + 170 + 140 + 220 + 130 + 120;

// How long a task has been sitting. Ops thinks in elapsed time, not clock time — and an
// hours-old booking task is the thing that quietly loses a truck, so it earns colour.
const AMBER_MINS = 120;
const RED_MINS = 360;

const ageMinutes = (createdAt?: string | null): number | null =>
  createdAt ? dayjs().diff(dayjs(createdAt), "minute") : null;

const dash = (v?: string | null) => v || <span style={{ color: "#bbb" }}>—</span>;

// Served a page at a time: the inbox can hold thousands of tasks, and the tab badge only needs the
// server's total count.
const PAGE_SIZE = 50;

const PendingTasksPanel: React.FC<Props> = ({ onCountChange, onChanged }) => {
  const [tasks, setTasks] = useState<PendingManualOp[]>([]);
  const [loading, setLoading] = useState(false);
  const [expressOnly, setExpressOnly] = useState(false);
  const [confirming, setConfirming] = useState<PendingManualOp | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await listPendingTasks({
      express: expressOnly || undefined,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    });
    setLoading(false);
    if (res && !res.results.length && page > 1 && res.count > 0) {
      setPage(Math.ceil(res.count / PAGE_SIZE));
      return;
    }
    if (res) {
      // Oldest first: the task that has waited longest is the one at risk.
      setTasks(
        [...res.results].sort(
          (a, b) => dayjs(a.created_at).valueOf() - dayjs(b.created_at).valueOf()
        )
      );
      setTotal(res.count);
      onCountChange?.(res.count);
    }
  }, [onCountChange, expressOnly, page]);

  useEffect(() => {
    load();
  }, [load]);

  const onConfirmed = () => {
    load();
    onChanged?.();
  };

  const columns: ColumnsType<PendingManualOp> = [
    {
      title: "Task",
      dataIndex: "op",
      width: 100,
      render: (op) => {
        const t = OP_TAG[op] || { color: "default", label: op };
        return <Tag color={t.color}>{t.label}</Tag>;
      },
    },
    {
      title: "What to do",
      dataIndex: "task",
      width: 320,
      render: (t) => <span style={{ fontSize: 13 }}>{t}</span>,
    },
    {
      title: "Provider",
      key: "provider",
      width: 130,
      render: (_, r) => (r.booking.provider_type || "—").replace(/_/g, " "),
    },
    {
      title: "SO / Lead",
      key: "order",
      width: 170,
      render: (_, r) => (
        <div>
          <Space size={4}>
            {r.booking.sales_order_id ? (
              <Link to={`/oms/${r.booking.sales_order_id}`}>
                {r.booking.zoho_salesorder_number || `SO #${r.booking.sales_order_id}`}
              </Link>
            ) : (
              dash(r.booking.zoho_salesorder_number)
            )}
            <ExpressTag show={r.booking.is_express} />
            <UnloadingTag show={r.booking.is_unloading} />
          </Space>
          <div style={{ fontSize: 12, color: "#888" }}>{dash(r.booking.lead_id)}</div>
        </div>
      ),
    },
    { title: "Client", key: "client", width: 140, render: (_, r) => dash(r.booking.client_name) },
    {
      title: "Route",
      key: "route",
      width: 220,
      render: (_, r) => (
        <span style={{ fontSize: 12 }}>
          {r.booking.origin_label || "—"} <span style={{ color: "#bbb" }}>→</span>{" "}
          {r.booking.destination_label || "—"}
        </span>
      ),
    },
    {
      title: "Waiting",
      dataIndex: "created_at",
      width: 130,
      render: (v) => {
        if (!v) return "—";
        const mins = ageMinutes(v) ?? 0;
        const color = mins >= RED_MINS ? "#cf1322" : mins >= AMBER_MINS ? "#d48806" : undefined;
        return (
          <Tooltip title={`${fmtIST(v, "DD MMM YYYY HH:mm")} IST`}>
            <span style={{ color, fontWeight: color ? 600 : undefined }}>
              {dayjs(v).fromNow(true)}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      width: 120,
      fixed: "right",
      render: (_, r) => (
        <Button type="primary" size="small" onClick={() => setConfirming(r)}>
          Confirm
        </Button>
      ),
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 12 }}>
        <Tooltip title="Refresh">
          <Button icon={<ReloadOutlined />} onClick={load} />
        </Tooltip>
        <Tooltip title="Show express orders only">
          <Space size={4}>
            <Switch
              size="small"
              checked={expressOnly}
              onChange={(v) => {
                setExpressOnly(v);
                setPage(1);
              }}
            />
            <span style={{ fontSize: 13 }}>Express only</span>
          </Space>
        </Tooltip>
        <span style={{ color: "#888", fontSize: 13 }}>
          {tasks.length} task{tasks.length === 1 ? "" : "s"} awaiting confirmation · oldest first
        </span>
      </Space>

      <Table
        rowKey="id"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={tasks}
        scroll={{ x: TABLE_WIDTH }}
        onRow={(r) => {
          const mins = ageMinutes(r.created_at) ?? 0;
          return {
            style: {
              background:
                mins >= RED_MINS ? "#fff1f0" : mins >= AMBER_MINS ? "#fffbe6" : undefined,
            },
          };
        }}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total,
          showSizeChanger: false,
          hideOnSinglePage: true,
          onChange: setPage,
        }}
        locale={{ emptyText: "No pending manual tasks 🎉" }}
      />

      <ConfirmOpModal
        open={!!confirming}
        task={confirming}
        onClose={() => setConfirming(null)}
        onConfirmed={onConfirmed}
      />
    </div>
  );
};

export default PendingTasksPanel;
